/**
 * Every Admin API endpoint this CMS uses, one function per route.
 *
 * The route list is the source of truth from personamatrix_cms_apis — see
 * `app/routers/*.py` or http://127.0.0.1:8002/docs. Nothing here calls a route
 * the backend does not expose.
 *
 * Two shapes worth remembering when wiring a page:
 *   - `/persona` is singular, and Core returns *every* persona: no q/limit/offset.
 *   - `/characters` and `/relationships` both **require** a `collection_id`;
 *     Core has no cross-collection listing for either.
 */

import { api } from './client';
import { audioFileName } from '@/lib/microphone';
import type {
  ProvisionalCharacter,
  ProvisionalConfirmPayload,
  ProvisionalConfirmResult,
  FeedbackCategory,
  FeedbackItem,
  FeedbackPage,
  FeedbackPriority,
  FeedbackStatus,
  AdminLogoutResponse,
  CharacterObjectAssociation,
  CharacterProductMapping,
  CharacterProductMappingCreatePayload,
  CharacterProductMappingUpdatePayload,
  CommerceSearchResponse,
  CommerceStore,
  CommerceStoreCreatePayload,
  CommerceStoreUpdatePayload,
  EnrichedProductMatch,
  ObjectTrack,
  ProductMatch,
  UcpManifest,
  UcpPlatformProfile,
  UcpSearchFilters,
  UcpSearchResponse,
  VideoCommerceData,
  AdminRegisterResponse,
  AdminTokenResponse,
  AdminUserProfile,
  AuditLogEntry,
  AuthenticatedUser,
  CharacterListParams,
  CharacterListResponse,
  CharacterMergePayload,
  CharacterMergeResult,
  CharacterPublicationPayload,
  CharacterPublicationResult,
  CharacterRenamePayload,
  CharacterRenameResult,
  CastAnchor,
  CastAnchorReferenceType,
  CastAnchorUploadResult,
  CastOverviewResponse,
  CastCredit,
  CastCreditCreatePayload,
  CastCreditUpdatePayload,
  TmdbCastResponse,
  WardrobeResponse,
  CharacterSplitPayload,
  CharacterSplitResult,
  CmsAnnotationUpdatePayload,
  Collection,
  CollectionCreatePayload,
  CollectionRelationship,
  DashboardResponse,
  DeleteResponse,
  NamingRerunResult,
  IngestReceipt,
  KnowledgeCreatePayload,
  CharacterVoice,
  CharacterVoiceClone,
  VoiceProvidersResponse,
  CharacterVoiceListResponse,
  CharacterPersona,
  ConversationAudioTurnPayload,
  ConversationMessagePayload,
  ConversationReply,
  ConversationTurn,
  CollectionTimeline,
  KnowledgeEntry,
  KnowledgeSearchResponse,
  Look,
  LookCreatePayload,
  LookUpdatePayload,
  MediaAsset,
  ObjectSearchResponse,
  Persona,
  PersonaCreatePayload,
  PersonaUpdatePayload,
  ProcessingJob,
  ProductObjectMapping,
  ProductObjectMappingCreatePayload,
  ProductObjectMappingPage,
  ReviewQueueItem,
  ReviewDecisionValue,
  Scene,
  SceneReviewPayload,
  SceneSearchResponse,
  SearchSource,
  StoryEventsResponse,
  NarrativeScene,
  UnifiedSearchResponse,
  TimelineEntry,
  VoiceProfile,
  VoiceProfileReviewPayload,
  SpeechAgentProfile,
  VoiceSourceChoice,
} from './types';

// --- Authentication ---------------------------------------------------------
export const authApi = {
  /** POST /api/v1/admin/auth/login — exchanges credentials for a Supabase session. */
  login: (email: string, password: string) =>
    api.post<AdminTokenResponse>('/auth/login', { email, password }, { auth: false }),

  /** POST /api/v1/admin/auth/register — creates the Supabase identity + CMS profile. */
  register: (email: string, password: string, fullName?: string) =>
    api.post<AdminRegisterResponse>(
      '/auth/register',
      { email, password, full_name: fullName || null },
      { auth: false }
    ),

  /** POST /api/v1/admin/auth/logout — revokes the Supabase session globally. */
  logout: () => api.post<AdminLogoutResponse>('/auth/logout'),

  /** GET /api/v1/admin/auth/me — token identity merged with the local profile. */
  me: () => api.get<AuthenticatedUser>('/auth/me'),
};

// --- Admin users ------------------------------------------------------------
//
// The operator directory, and the reason a governance row can name a person.
//
// A review item is stamped `reviewer_id = current_user.id` and an audit row
// `actor_id = current_user.id`, where that id is the **Supabase** identity from
// the access token. `AdminUserProfile` carries `supabase_user_id` alongside the
// local profile id, so either form of id resolves. See `lib/admin-users.ts`.
//
// This was previously recorded as a missing capability. It is not missing; it
// was simply never called.
export const adminUsersApi = {
  /** GET /api/v1/admin/users — every admin profile. */
  list: () => api.get<AdminUserProfile[]>('/users'),

  /** GET /api/v1/admin/users/{id} — one profile, when only one is needed. */
  get: (adminUserId: string) => api.get<AdminUserProfile>(`/users/${adminUserId}`),

  /**
   * GET /api/v1/admin/users/me — the caller's own profile.
   *
   * 404 means the signed-in operator has no local profile yet, which is a real
   * state: the Supabase identity exists and the admin row has not been created.
   */
  me: () => api.get<AdminUserProfile>('/users/me'),
};

// --- Dashboard --------------------------------------------------------------
export const dashboardApi = {
  /** GET /api/v1/admin/dashboard — counts across both databases + audit feed. */
  get: (activityLimit = 10) =>
    api.get<DashboardResponse>('/dashboard', { query: { activity_limit: activityLimit } }),
};

// --- Collections ------------------------------------------------------------
export const collectionsApi = {
  /** GET /api/v1/admin/collections */
  list: (
    params: {
      search?: string;
      include_deleted?: boolean;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<Collection[]>('/collections', { query: params }),

  /** GET /api/v1/admin/collections/{id} */
  get: (id: string) => api.get<Collection>(`/collections/${id}`),

  /** POST /api/v1/admin/collections — proxied to Core, which provisions Qdrant + FalkorDB. */
  create: (payload: CollectionCreatePayload) => api.post<Collection>('/collections', payload),

  /**
   * PATCH /api/v1/admin/collections/{id}/cms — the CMS editorial layer.
   * This is the supported way to edit a collection: the plain PATCH is
   * deprecated backend-side and 403s while CORE_DATABASE_READ_ONLY is set.
   */
  updateCms: (id: string, payload: CmsAnnotationUpdatePayload) =>
    api.patch<Collection>(`/collections/${id}/cms`, payload),

  /** DELETE /api/v1/admin/collections/{id} — CMS soft-hide, Core's row survives. */
  hide: (id: string) => api.delete<DeleteResponse>(`/collections/${id}`),

  /** POST /api/v1/admin/collections/{id}/restore */
  restore: (id: string) => api.post<Collection>(`/collections/${id}/restore`),
};

// --- Review manifest (pipeline quality, live-recomputed on every read) ------
export const reviewManifestApi = {
  /**
   * GET /api/v1/admin/collections/{id}/videos/{videoId}/review
   *
   * The post-ingest quality report layered by severity:
   *   overall_status   — "green" | "yellow" | "red"
   *   needs_attention  — unnamed speaking characters + low-confidence names
   *   pipeline_warnings — non-fatal sub-stage failures recorded during ingest
   *   metrics          — full scorecard (attribution_pct, naming_coverage_pct, …)
   *
   * Recomputed live on every read — never stale. `videoId` is Core's
   * external_video_id (e.g. "superman_2025"), not the UUID.
   *
   * 404 means no ingested video was found for the pair — a real state
   * (the collection exists but hasn't been ingested yet), not an error.
   */
  get: (collectionId: string, videoId: string) =>
    api.get<{
      overall_status: 'green' | 'yellow' | 'red';
      needs_attention: Array<{
        character_id: string;
        canonical_name: string;
        reason: string;
        thumbnail_url: string | null;
        sample_dialogue: string[];
      }>;
      pipeline_warnings: string[];
      metrics: {
        attribution_pct: number | null;
        naming_coverage_pct: number | null;
        fusion_pct: number | null;
        status: string;
        warnings: string[];
        [key: string]: unknown;
      };
    }>(`/collections/${collectionId}/videos/${videoId}/review`),
};

// --- Media assets -----------------------------------------------------------
export const mediaAssetsApi = {
  /**
   * GET /api/v1/admin/media-assets — reads Core's `media_assets` table, so each
   * row carries its `cms` annotation (title/description live there, not in Core).
   */
  list: (
    params: {
      collection_id?: string;
      status?: string;
      include_deleted?: boolean;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<MediaAsset[]>('/media-assets', { query: params }),

  /** GET /api/v1/admin/media-assets/{id} */
  get: (id: string) => api.get<MediaAsset>(`/media-assets/${id}`),

  /**
   * GET /api/v1/admin/collections/{id}/videos — proxied to Core.
   *
   * Prefer this over `list({ collection_id })` for anything order-dependent:
   * Core computes `episode_index` per request from (season, episode, created_at),
   * so a corrected episode number renumbers the list immediately. The trade-off
   * is that Core's response has no `cms` annotation attached.
   */
  listForCollection: (collectionId: string) =>
    api.get<MediaAsset[]>(`/collections/${collectionId}/videos`),

  /**
   * PATCH /api/v1/admin/media-assets/{id}/cms — the CMS editorial layer.
   * This is the supported way to edit an asset: the plain PATCH is deprecated
   * backend-side and 403s while CORE_DATABASE_READ_ONLY is set.
   */
  updateCms: (id: string, payload: CmsAnnotationUpdatePayload) =>
    api.patch<MediaAsset>(`/media-assets/${id}/cms`, payload),

  /** DELETE /api/v1/admin/media-assets/{id} — CMS soft-hide; Core's row survives. */
  hide: (id: string) => api.delete<DeleteResponse>(`/media-assets/${id}`),

  /** GET /api/v1/admin/media-assets/{id}/processing-jobs — resolved via video_id. */
  jobs: (id: string, params: { limit?: number; offset?: number } = {}) =>
    api.get<ProcessingJob[]>(`/media-assets/${id}/processing-jobs`, { query: params }),
};

// --- Processing jobs (read-only upstream, except cancel) ---------------------
// Every column belongs to the Celery worker executing the job, so there is no
// create/update/retry here: the backend answers 501 for those and points at
// Core, because writing the table would report success while no worker ever
// picked the job up. Cancel is different: it proxies Core's own
// `POST /videorag/jobs/{id}/cancel`, which revokes the Celery task and lets
// Core write `cancelled` to its own row -- a real action, not a table write.
export const processingJobsApi = {
  /** GET /api/v1/admin/processing-jobs — newest first. `active` = queued or running. */
  list: (
    params: {
      active?: boolean;
      status?: string;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<ProcessingJob[]>('/processing-jobs', { query: params }),

  /** GET /api/v1/admin/processing-jobs/{id} */
  get: (id: string) => api.get<ProcessingJob>(`/processing-jobs/${id}`),

  /** GET /api/v1/admin/processing-jobs/{id}/status — status, progress, stage, error. */
  status: (id: string) => api.get<ProcessingJob>(`/processing-jobs/${id}/status`),

  /**
   * POST /api/v1/admin/processing-jobs/{id}/cancel — 409 if the job is not
   * queued/running (Core has already finished, failed or cancelled it), 404
   * if the job id does not exist.
   */
  cancel: (id: string) => api.post<ProcessingJob>(`/processing-jobs/${id}/cancel`),
};

// --- Scenes -----------------------------------------------------------------
export const scenesApi = {
  /**
   * GET /api/v1/admin/scenes — table-backed, in `scene_index` order, with each
   * row's `cms` annotation (mood/location live in `cms.attributes`).
   *
   * `mood`, `location` and `q` are a **server-side** search over that annotation,
   * which is the only place those values exist — Core's `scenes` table has no
   * column for either. Passing any of them switches the backend from a Core-first
   * listing to an annotation-first search, so a match is found even when the scene
   * is not in the page that would otherwise have been fetched. Filtering the
   * loaded rows in the browser instead is what made metadata search silently
   * return nothing outside the current page.
   *
   * Combine with `asset_id` to search inside one video; omit it to search all.
   */
  list: (
    params: {
      asset_id?: string;
      mood?: string;
      location?: string;
      q?: string;
      include_deleted?: boolean;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<Scene[]>('/scenes', { query: params }),

  /** GET /api/v1/admin/scenes/{id} */
  get: (id: string) => api.get<Scene>(`/scenes/${id}`),

  /**
   * GET /api/v1/admin/collections/{cid}/videos/{videoId}/scenes — proxied to Core.
   *
   * `videoId` is the asset's **`external_video_id`** (e.g. `animal_001`), not its
   * UUID: Core resolves the asset from the pair. No `cms` annotation on these rows.
   */
  listForVideo: (collectionId: string, videoId: string) =>
    api.get<Scene[]>(`/collections/${collectionId}/videos/${videoId}/scenes`),

  /**
   * PATCH /api/v1/admin/scenes/{id}/review — proxied to Core, which owns
   * `scenes.approval_status`. **The only writer of scene approval.**
   *
   * Do not use `updateCms` for approval: that writes the CMS annotation's
   * separate `editorial_status`, which Core's review manifest never reads.
   * Returns Core's full scene, so the row can be re-rendered without a refetch.
   */
  review: (id: string, payload: SceneReviewPayload) =>
    api.patch<Scene>(`/scenes/${id}/review`, payload),

  /**
   * PATCH /api/v1/admin/scenes/{id}/cms — mood, location and anything else Core
   * has no column for. Put mood/location in `attributes`.
   */
  updateCms: (id: string, payload: CmsAnnotationUpdatePayload) =>
    api.patch<Scene>(`/scenes/${id}/cms`, payload),

  /** DELETE /api/v1/admin/scenes/{id} — CMS soft-hide; Core's row survives. */
  hide: (id: string) => api.delete<DeleteResponse>(`/scenes/${id}`),
};

// --- Personas ---------------------------------------------------------------
export const personasApi = {
  /**
   * GET /api/v1/admin/persona
   *
   * `collection_id` is **Core's own filter**, not one applied here: Core joins
   * `personas.character_id -> characters.collection_id`. Pass it to show one
   * title's personas, omit it for "All collections".
   *
   * Worth passing whenever a collection is in hand. Unfiltered, Core returns
   * every persona ever created — measured at 236, most of them placeholder
   * shells named after characters, including near-duplicates left behind by
   * internal test collections.
   *
   * A persona with no bound character carries no collection and therefore
   * appears only in the unfiltered list. That is a property of the data, not a
   * bug: the collection is reached *through* the character.
   */
  list: (params: { collection_id?: string | null; published_only?: boolean } = {}) =>
    api.get<Persona[]>('/persona', { query: params }),

  /** GET /api/v1/admin/persona/{id} */
  get: (id: string) => api.get<Persona>(`/persona/${id}`),

  /** POST /api/v1/admin/persona */
  create: (payload: PersonaCreatePayload) => api.post<Persona>('/persona', payload),

  /**
   * PATCH /api/v1/admin/persona/{id}
   *
   * Send `name` only when a human actually renamed the persona — Core sets
   * `name_is_editorial` whenever the field is present, permanently freezing the
   * name against later automated naming passes.
   */
  update: (id: string, payload: PersonaUpdatePayload) =>
    api.patch<Persona>(`/persona/${id}`, payload),

  /** DELETE /api/v1/admin/persona/{id} — a real delete, performed by Core. */
  delete: (id: string) => api.delete<DeleteResponse>(`/persona/${id}`),
};

// --- Characters -------------------------------------------------------------
export const charactersApi = {
  /**
   * GET /api/v1/admin/characters?collection_id= — collection_id is required.
   *
   * Returns Core's **paginated envelope**, `{total, limit, offset, characters[]}`,
   * not a bare array. Read `.characters` for the rows and `.total` for the count.
   */
  list: (collectionId: string, params: CharacterListParams = {}) =>
    api.get<CharacterListResponse>('/characters', {
      query: { collection_id: collectionId, ...params },
    }),

  /** PATCH /api/v1/admin/characters/{id} — rename (editorial, sticky). */
  rename: (id: string, payload: CharacterRenamePayload) =>
    api.patch<CharacterRenameResult>(`/characters/${id}`, payload),

  /**
   * PATCH /api/v1/admin/characters/{id}/publication — publish or unpublish.
   *
   * Writes Core's `characters.status`, which is the **authoritative** publication
   * gate: publishing makes the character's persona appear in
   * `GET /persona/?published_only=true` and answerable by
   * `POST /conversation/message`; unpublishing stops both, immediately.
   *
   * Not to be confused with writing `editorial_status` through the `/cms`
   * annotation route — nothing consumer-facing reads that field.
   *
   * Send `in_review` to unpublish. `draft` is accepted by the API but means
   * "the pipeline has not finished with this yet", which is not true of
   * something a human withdrew.
   */
  setPublication: (id: string, payload: CharacterPublicationPayload) =>
    api.patch<CharacterPublicationResult>(`/characters/${id}/publication`, payload),

  /** POST /api/v1/admin/characters/{id}/merge — path id is the survivor. */
  merge: (id: string, payload: CharacterMergePayload) =>
    api.post<CharacterMergeResult>(`/characters/${id}/merge`, payload),

  /** POST /api/v1/admin/characters/{id}/split — needs at least one evidence id. */
  split: (id: string, payload: CharacterSplitPayload) =>
    api.post<CharacterSplitResult>(`/characters/${id}/split`, payload),

  /**
   * GET /api/v1/admin/characters/provisional?collection_id= — recurring faces that
   * match no credited character, kept by Core as unconfirmed identities. A bare
   * list, not a paginated envelope. The review queue for them; never mixed into
   * `list`, which stays named-only.
   */
  listProvisional: (collectionId: string) =>
    api.get<ProvisionalCharacter[]>('/characters/provisional', {
      query: { collection_id: collectionId },
    }),

  /**
   * POST /api/v1/admin/characters/{id}/confirm-provisional — "these crops are one
   * real character", optionally with a name. To say it is an existing credited
   * character instead, `merge` it into that character.
   */
  confirmProvisional: (id: string, payload: ProvisionalConfirmPayload = {}) =>
    api.post<ProvisionalConfirmResult>(`/characters/${id}/confirm-provisional`, payload),
};

// --- Voice profiles ---------------------------------------------------------
export const voiceProfilesApi = {
  /**
   * GET /api/v1/admin/persona/{id}/voice-profiles
   *
   * `[]` is the normal answer for a persona with no clones — most personas use a
   * premade provider voice. Not an error, and not a missing persona.
   */
  list: (personaId: string) => api.get<VoiceProfile[]>(`/persona/${personaId}/voice-profiles`),

  /**
   * PATCH /api/v1/admin/persona/{id}/voice-profiles/{profileId} — QA verdict only.
   *
   * `clone_status` is not writable: it reports what the cloning pipeline did, so a
   * human asserting `ready` over a failed clone would make the record lie.
   */
  review: (personaId: string, profileId: string, payload: VoiceProfileReviewPayload) =>
    api.patch<VoiceProfile>(`/persona/${personaId}/voice-profiles/${profileId}`, payload),

  /**
   * POST /api/v1/admin/persona/{id}/clone-voice — multipart.
   *
   * Returns the updated **persona** (its `voice_id` is what changed for TTS), and
   * Core writes a VoiceProfile alongside it. Read the profile back from `list`.
   * mp3/wav/m4a/ogg/flac; 30s–3min of clean speech per sample.
   */
  clone: (personaId: string, samples: File[]) => {
    const body = new FormData();
    for (const sample of samples) body.append('voice_files', sample, sample.name);
    return api.postForm<Persona>(`/persona/${personaId}/clone-voice`, body);
  },
};

// --- Cast anchors / photo references ----------------------------------------
export const castAnchorsApi = {
  /**
   * GET /api/v1/admin/collections/{id}/cast-anchors
   *
   * Grouped by character, each group holding every reference photo with its own
   * `reference_type`, `provenance`, `qa_status` and `version`.
   */
  list: (collectionId: string) =>
    api.get<CastAnchor[]>(`/collections/${collectionId}/cast-anchors`),

  /**
   * POST /api/v1/admin/collections/{id}/cast-anchors — multipart.
   *
   * `photos` is appended repeatedly under the same field name because Core's
   * route takes `List[UploadFile]`; one combined field would bind only the last
   * file. Returns a summary, not a list of rows: a photo with no detectable face
   * is *counted and skipped*, so check `photos_saved` before reporting success.
   */
  upload: (
    collectionId: string,
    input: {
      character_name: string;
      photos: File[];
      person_name?: string | null;
      reference_type?: CastAnchorReferenceType;
      provenance?: string | null;
      /**
       * One promotional headshot. A SEPARATE field, not another entry in
       * `photos`: Core stores it and never embeds it, and putting a studio
       * portrait into the matcher is the documented failure it exists to avoid.
       */
      display_photo?: File | null;
      /**
       * Up to four clips of the character's voice as heard in this film. Used
       * for speaker attribution during ingest. **Not** a TTS clone.
       */
      voice_samples?: File[];
    }
  ) => {
    const body = new FormData();
    body.append('character_name', input.character_name);
    if (input.person_name) body.append('person_name', input.person_name);
    if (input.reference_type) body.append('reference_type', input.reference_type);
    if (input.provenance) body.append('provenance', input.provenance);
    for (const photo of input.photos) body.append('photos', photo, photo.name);
    if (input.display_photo) {
      body.append('display_photo', input.display_photo, input.display_photo.name);
    }
    for (const clip of input.voice_samples ?? []) body.append('voice_samples', clip, clip.name);
    return api.postForm<CastAnchorUploadResult>(`/collections/${collectionId}/cast-anchors`, body);
  },

  /**
   * GET /api/v1/admin/collections/{id}/cast-overview
   *
   * TMDB's credited cast merged with the operator's uploads, plus what ingestion
   * produced for each character.
   *
   * `refresh` DEFAULTS TO FALSE AND MUST STAY THAT WAY on every automatic call.
   * `refresh=true` makes Core re-fetch the credited cast and recompute a face
   * embedding per headshot, for every credited member — minutes on a large cast.
   * It belongs behind an explicit button press, never on mount, a re-render or a
   * retry.
   *
   * A plain read is still not free the *first* time: Core's condition is
   * `refresh or not cache`, so a collection nobody has looked up resolves its
   * title and populates the cast cache on the first read. That is deliberate —
   * it is what makes "add anchors before you ingest" possible, since before the
   * first ingest `tmdb_id` is NULL and the cast is genuinely unknown.
   */
  overview: (collectionId: string) =>
    api.get<CastOverviewResponse>(`/collections/${collectionId}/cast-overview`),

  /**
   * POST /api/v1/admin/collections/{id}/cast-overview/refresh
   *
   * Trigger a TMDB refresh. Separated from GET so that a plain read never
   * commits to the database (EX-20).
   */
  refreshOverview: (collectionId: string, options?: { force?: boolean }) =>
    api.post<{ cached_at: string | null; members: number }>(
      `/collections/${collectionId}/cast-overview/refresh`,
      options?.force ? { force: true } : {},
    ),

  /**
   * DELETE /api/v1/admin/collections/{id}/cast-anchor-photos/{photoId}
   *
   * One identification photo, not the character. The embedding lives on that row,
   * so removing the row is what stops the photo being matched; Core leaves the
   * stored image itself in place on purpose.
   */
  deletePhoto: (collectionId: string, photoId: string) =>
    api.delete<DeleteResponse>(`/collections/${collectionId}/cast-anchor-photos/${photoId}`),

  /**
   * DELETE /api/v1/admin/collections/{id}/cast-anchors/display-photo
   *
   * Clears the promotional still across every row for the character, leaving the
   * identification photos alone. `character_name` goes in the query string
   * because a role name is free text and can contain a slash, which no single
   * path segment can carry.
   */
  clearDisplayPhoto: (collectionId: string, characterName: string) =>
    api.delete<DeleteResponse>(`/collections/${collectionId}/cast-anchors/display-photo`, {
      query: { character_name: characterName },
    }),

  /**
   * DELETE /api/v1/admin/collections/{id}/cast-anchors/voice
   *
   * Clears the reference clip AND its voiceprint on every row for the character —
   * both, because leaving the print would keep the ingest matching against a
   * recording the operator has withdrawn.
   *
   * It does not touch any cloned TTS voice, because a cast anchor never produced
   * one. That is a `ClonedVoice`, built from the sample Core extracted from the
   * character's own attributed dialogue, and it is removed elsewhere.
   */
  clearVoice: (collectionId: string, characterName: string) =>
    api.delete<DeleteResponse>(`/collections/${collectionId}/cast-anchors/voice`, {
      query: { character_name: characterName },
    }),

  /**
   * POST /api/v1/admin/collections/{id}/naming/rerun
   *
   * Applies anchors uploaded after an ingest without re-processing any video.
   * Core resolves names over the stored face and utterance data, then rejoins the
   * splits a photo-resolved name tends to create.
   *
   * `newly_named` and `splits_merged` describe naming and nothing else. They are
   * not evidence of chatability or of a ready voice: this pass does not
   * re-detect faces, re-attribute speech, apply operator voice anchors (the
   * ingest worker does that, and nothing else calls it) or create personas.
   *
   * 404 means the collection has no ingested asset — the honest answer, and the
   * anchors will be used by the ingest itself when it runs.
   */
  rerunNaming: (collectionId: string, options?: { video_id?: string | null }) =>
    api.post<NamingRerunResult>(`/collections/${collectionId}/naming/rerun`, undefined, {
      query: options?.video_id ? { video_id: options.video_id } : undefined,
    }),
};

// --- Knowledge --------------------------------------------------------------
export const knowledgeApi = {
  /**
   * GET /api/v1/admin/knowledge?persona_id= — one persona's entries.
   *
   * `persona_id` is required, not a filter: knowledge is persona-scoped in Core
   * and there is no cross-persona listing. Calling this without one used to
   * return a 422 the page could do nothing about.
   */
  list: (personaId: string) =>
    api.get<KnowledgeEntry[]>('/knowledge', { query: { persona_id: personaId } }),

  /** GET /api/v1/admin/knowledge/{id} */
  get: (id: string) => api.get<KnowledgeEntry>(`/knowledge/${id}`),

  /** POST /api/v1/admin/knowledge — Core chunks and embeds the text into Qdrant. */
  create: (payload: KnowledgeCreatePayload) => api.post<KnowledgeEntry>('/knowledge', payload),

  /** DELETE /api/v1/admin/knowledge/{id} — removes the row and its vectors. */
  delete: (id: string) => api.delete<DeleteResponse>(`/knowledge/${id}`),

  /**
   * GET /api/v1/admin/knowledge/search/{persona_id}?q= — a real Qdrant vector
   * search, publication-gated upstream. Core requires 3+ characters and a UUID
   * persona id; both are enforced by the backend before the call is made.
   */
  search: (personaId: string, q: string) =>
    api.get<KnowledgeSearchResponse>(`/knowledge/search/${personaId}`, { query: { q } }),
};

// --- Relationships (read-only upstream) -------------------------------------
export const relationshipsApi = {
  /** GET /api/v1/admin/relationships?collection_id=[&asset_id=] — collection_id is required. */
  list: (collectionId: string, assetId?: string | null) =>
    api.get<CollectionRelationship[]>('/relationships', {
      query: { collection_id: collectionId, ...(assetId ? { asset_id: assetId } : {}) },
    }),

  /**
   * GET /api/v1/admin/relationships/states?collection_id=[&viewer_episode_index=]
   *
   * Time-aware relationship states from character_relationship_states.
   * Each row holds valid_from, superseded_by, caused_by_story_event_id,
   * evidence, certainty_status, tone and directed labels — the full temporal
   * history of how each pair's relationship evolved.
   *
   * Omit viewer_episode_index for the current (latest non-superseded) state.
   */
  listStates: (collectionId: string, viewerEpisodeIndex?: number | null) =>
    api.get<import('./types').RelationshipState[]>('/relationships/states', {
      query: {
        collection_id: collectionId,
        ...(viewerEpisodeIndex != null ? { viewer_episode_index: viewerEpisodeIndex } : {}),
      },
    }),
};

// --- Story events (read-only upstream) --------------------------------------
//
// Core's narrative-event primitive: "X did Y to Z" with a scene id and
// millisecond bounds. Unlike `relationshipsApi` above, these ARE scene- and
// time-scoped — the per-scene interaction record §16 records as missing from
// the relationship table.
//
// Events are returned already grouped by scene, so a consumer never has to
// group them itself and risk disagreeing with the next consumer about how.
export const storyEventsApi = {
  /**
   * GET /api/v1/admin/story-events?collection_id=&video_id=[&source_kind=]
   *
   * Per-video story events, grouped by narrative scene. `video_id` is Core's
   * `external_video_id` or the asset UUID.
   */
  list: (collectionId: string, videoId: string, sourceKind?: string) =>
    api.get<StoryEventsResponse>('/story-events', {
      query: { collection_id: collectionId, video_id: videoId, source_kind: sourceKind },
    }),

  /**
   * GET /api/v1/admin/story-events/collection?collection_id=
   *
   * Collection-wide story events (all assets, all episodes). Returns
   * { collection_id, total_events, total_assets, assets: StoryEventsResponse[] }.
   * Used by the Temporal Network to explore events across a multi-episode series.
   */
  listCollection: (
    collectionId: string,
    params: { sourceKind?: string; observationType?: string; eventType?: string } = {}
  ) =>
    api.get<{ collection_id: string; total_events: number; total_assets: number; assets: StoryEventsResponse[] }>(
      '/story-events/collection',
      { query: { collection_id: collectionId, source_kind: params.sourceKind, observation_type: params.observationType, event_type: params.eventType } }
    ),

  /**
   * GET /api/v1/admin/story-events/{event_id}/evidence-trace?collection_id=
   *
   * Full evidence trace for one story event: every evidence_ref resolved
   * (utterance text + timestamps), plus linked_event_ids and alternatives.
   * Used by the Temporal Network inspector "View evidence" action.
   */
  getEvidenceTrace: (collectionId: string, eventId: string) =>
    api.get<{
      event_id: string;
      found: boolean;
      event_type: string;
      source_kind: string;
      source_utterance_id: string | null;
      scene_id: string | null;
      evidence_refs: Array<{ kind: string; id: string; resolved: boolean; role: string }>;
      linked_event_ids: string[];
      alternatives: unknown[];
    }>(`/story-events/${eventId}/evidence-trace`, {
      query: { collection_id: collectionId },
    }),

  /**
   * GET /api/v1/admin/story-events/evidence/resolve?collection_id=&ids=
   *
   * Batch-resolves evidence reference UUIDs to utterance records
   * (text, start, end, speaker_name). Up to ~20 IDs per call.
   */
  resolveEvidence: (collectionId: string, ids: string[]) =>
    api.get<Record<string, { video_id: string; start: number; end: number; text: string; speaker_name: string | null }>>(
      '/story-events/evidence/resolve',
      { query: { collection_id: collectionId, ids } }
    ),

  /**
   * GET /api/v1/admin/story-events/film-spine?collection_id=
   *
   * Film spine: logline, character_arcs, act_structure, turning_points.
   * Used by the Temporal Network Zone A header.
   */
  getFilmSpine: (collectionId: string) =>
    api.get<{
      collection_id: string;
      asset_id: string;
      logline: string | null;
      character_arcs: Array<{ character: string; goal: string; arc: string; scene_indices: number[] }>;
      conflicts: string[];
      act_structure: unknown;
      turning_points: Array<{ scene_index: number; description: string; significance?: string }>;
      themes: string[];
    }>('/story-events/film-spine', { query: { collection_id: collectionId } }),

  /**
   * GET /api/v1/admin/story-events/series-arc?collection_id=
   *
   * Series arc for multi-episode collections. 404 for single-episode films.
   */
  getSeriesArc: (collectionId: string) =>
    api.get<{ arc_type?: string; overall_premise?: string; character_arcs?: unknown[] }>(
      '/story-events/series-arc',
      { query: { collection_id: collectionId } }
    ),
};

// --- Character emotions (Core, collection-scoped) ----------------------------
export const characterEmotionsApi = {
  /**
   * GET /api/v1/admin/story-events/character-emotions?collection_id=
   *
   * Proxies Core's `GET /collections/{cid}/character-emotions`.
   * Closed vocabulary: angry / happy / sad / fear / surprise / disgust / neutral.
   * Scoped to a collection; optionally filtered by asset, character, or emotion label.
   */
  list: (
    collectionId: string,
    params: {
      character_id?: string | null;
      asset_id?: string | null;
      emotion?: string | null;
      limit?: number;
      offset?: number;
    } = {}
  ) =>
    api.get<import('./types').CharacterEmotion[]>('/story-events/character-emotions', {
      query: { collection_id: collectionId, ...params },
    }),
};

// --- Narrative scenes (dramaturgical, with real LLM summaries) ---------------
export const narrativeScenesApi = {
  /**
   * GET /api/v1/admin/scenes/narrative?collection_id=&video_id=
   *
   * Returns NarrativeScene rows with real LLM summaries. Shot-level scenes
   * have no summaries — use this for display.
   */
  list: (collectionId: string, videoId: string) =>
    api.get<NarrativeScene[]>('/scenes/narrative', {
      query: { collection_id: collectionId, video_id: videoId },
    }),
};

// --- Avatar assets (Core /avatar/*, proxied via the Admin API) ---------------
//
// Face images and voice clips extracted from ingested content, indexed by the
// contextual emotion label the pipeline assigned. Data is read-only; production
// happens in the ingest worker (avatar_emotion_frame_service).
//
// Routes are keyed on persona_id, not character_id. Resolve the persona_id via
// conversationApi.personaForCharacter() before calling these.
export const avatarApi = {
  /**
   * GET /api/v1/admin/personas/{personaId}/avatar
   *
   * Readiness summary: total frames by emotion, voice clip seconds by emotion,
   * clone_ready flag. Use before opening the gallery to decide whether there is
   * anything to show.
   */
  manifest: (personaId: string) =>
    api.get<{
      persona_id: string;
      character_id: string;
      collection_id: string;
      name: string;
      chat_ready: boolean;
      emotion_images: {
        total: number;
        by_emotion: Record<string, number>;
        by_face_expression: Record<string, number>;
      };
      voice: {
        master_seconds: number;
        clone_ready: boolean;
        clips: number;
        seconds_by_emotion: Record<string, number>;
      };
    }>(`/personas/${personaId}/avatar`),

  /**
   * GET /api/v1/admin/personas/{personaId}/avatar/images
   *
   * Paginated emotion-labelled face frames. Each carries:
   *   image_url      — S3 URL of the face crop
   *   emotion        — contextual label (happy/angry/sad/fear/surprise/disgust/neutral)
   *   face_expression — what the face actually shows (may differ from emotion)
   *   pose           — frontal | three_quarter | profile
   *   avatar_quality_score — VLM-rated suitability 0–1 (higher = better headshot)
   *   is_default     — exactly one frame per character is the recommended avatar base
   *   label_confidence — high | medium | low: how far to trust the emotion LABEL
   *   context        — triggering line + scene summary (optional, costs a join)
   */
  images: (
    personaId: string,
    params: {
      emotion?: string | null;
      limit?: number;
      offset?: number;
      include_context?: boolean;
    } = {}
  ) =>
    api.get<{
      persona_id: string;
      character_id: string;
      total: number;
      limit: number;
      offset: number;
      emotions_available: Record<string, number>;
      face_expressions_available: Record<string, number>;
      frames: Array<{
        frame_id: string;
        is_default: boolean;
        image_kind: string;
        emotion: string;
        emotion_raw: string | null;
        emotion_source: string;
        emotion_confidence: number | null;
        agreement: string;
        label_confidence: string;
        speaking: boolean;
        image_url: string;
        image_format: string;
        width: number;
        height: number;
        face_size_px: number;
        asset_id: string;
        episode_index: number | null;
        timestamp_seconds: number;
        identity_check: string | null;
        identity_score: number | null;
        sharpness: number | null;
        face_expression: string | null;
        face_expression_confidence: number | null;
        face_agrees: boolean | null;
        pose: string | null;
        avatar_quality_score: number | null;
        context: {
          line: string | null;
          scene_evidence: string | null;
          scene_summary: string | null;
          scene_index: number | null;
        } | null;
      }>;
    }>(`/personas/${personaId}/avatar/images`, { query: params }),

  /**
   * GET /api/v1/admin/personas/{personaId}/avatar/clips
   *
   * Paginated emotion-labelled voice clips. Each carries audio_url, emotion,
   * duration_seconds, speaking, and the transcribed line.
   */
  clips: (
    personaId: string,
    params: { emotion?: string | null; limit?: number; offset?: number } = {}
  ) =>
    api.get<{
      persona_id: string;
      character_id: string;
      total: number;
      limit: number;
      offset: number;
      clips: Array<{
        clip_id: string;
        emotion: string;
        emotion_source: string;
        duration_seconds: number;
        speaking: boolean;
        audio_url: string;
        line: string | null;
      }>;
    }>(`/personas/${personaId}/avatar/clips`, { query: params }),
};

// --- Character voices (Core `/voices/*`, via the Admin API) ------------------
//
// Distinct from `voiceProfilesApi` below, which is the *persona* clone audit
// trail. This is the character-scoped surface: which characters have a voice
// sample Core extracted from their own dialogue, and the clones made from it.
/**
 * Conversation — talking to a character from the CMS.
 *
 * Core keys a conversation on the **persona**, not the character. Resolve once
 * with `personaForCharacter` when a chat opens and reuse the id: that lookup
 * costs a full persona listing upstream (unpaginated, ~236 rows), so doing it
 * per message would make every turn pay for it.
 */
export const conversationApi = {
  /**
   * GET /api/v1/admin/characters/{id}/persona
   *
   * 404 means the character has no persona shell, so it cannot be chatted with
   * yet — a real state, not a missing character.
   */
  personaForCharacter: (characterId: string) =>
    api.get<CharacterPersona>(`/characters/${characterId}/persona`),

  /** GET /api/v1/admin/conversation/{personaId} — prior turns. */
  history: (personaId: string) => api.get<ConversationTurn[]>(`/conversation/${personaId}`),

  /** GET /api/v1/admin/conversation/{personaId}/suggested-questions */
  suggestedQuestions: (personaId: string) =>
    api.get<unknown>(`/conversation/${personaId}/suggested-questions`),

  /** POST /api/v1/admin/conversation/message — one turn. */
  send: (payload: ConversationMessagePayload) =>
    api.post<ConversationReply>('/conversation/message', payload),

  /**
   * POST /api/v1/admin/conversation/audio-message — one **spoken** turn.
   *
   * The call, as opposed to the chat. Core transcribes the recording, answers it,
   * and speaks the answer back in the character's cloned voice.
   *
   * Multipart, so it goes through `postForm`: `Content-Type` must be left for the
   * browser to generate, boundary included.
   *
   * Asynchronous exactly like `send` — the 201 carries the **transcript** on
   * `user_input` and an empty `persona_reply`. Poll `turn` for the answer and its
   * `audio_url`. Treating this body as the reply renders silence every time.
   */
  sendAudio: ({
    personaId,
    audio,
    language = 'en',
    responseLanguage,
    clientId,
    userName,
    viewerEpisodeIndex,
    viewerCurrentTimestamp,
  }: ConversationAudioTurnPayload) => {
    const form = new FormData();
    form.append('persona_id', personaId);
    // A filename is required for the browser to encode this as a file part rather
    // than a plain field, and Core keys its container sniffing off the extension —
    // so it is derived from the blob's own type. Hardcoding `.webm` sent Safari's
    // `audio/mp4` turns to ffmpeg labelled as something they are not.
    form.append('file', audio, audioFileName(audio.type));
    form.append('language', language);
    if (responseLanguage) form.append('response_language', responseLanguage);
    if (clientId) form.append('client_id', clientId);
    if (userName) form.append('user_name', userName);
    if (viewerEpisodeIndex != null) form.append('viewer_episode_index', String(viewerEpisodeIndex));
    if (viewerCurrentTimestamp != null)
      form.append('viewer_current_timestamp', String(viewerCurrentTimestamp));
    return api.postForm<ConversationReply>('/conversation/audio-message', form);
  },

  /**
   * GET /api/v1/admin/conversation/turn/{conversationId} — one turn, by id.
   *
   * What a call polls. Cheaper than `history` for that: history returns every prior
   * turn, so watching one row fill in would cost work proportional to the length of
   * the whole conversation.
   */
  turn: (conversationId: string) =>
    api.get<ConversationTurn>(`/conversation/turn/${conversationId}`),

  /** DELETE /api/v1/admin/conversation/turn/{conversationId} */
  deleteTurn: (conversationId: string) =>
    api.delete<unknown>(`/conversation/turn/${conversationId}`),
};

export const characterVoicesApi = {
  /** GET /api/v1/admin/voices/providers — `configured` means usable now; `default_clone_provider` is what Core would pick. */
  providers: () => api.get<VoiceProvidersResponse>('/voices/providers'),

  /**
   * GET /api/v1/admin/voices/characters?collection_id=
   *
   * `collection_id` is required: Core warns that unscoped this spans every
   * character in the catalogue.
   */
  list: (collectionId: string, onlyWithSample = true) =>
    api.get<CharacterVoiceListResponse>('/voices/characters', {
      query: { collection_id: collectionId, only_with_sample: onlyWithSample },
    }),

  /**
   * GET /api/v1/admin/characters/{id}/voice?collection_id=
   *
   * 404 means Core has no sample for this character — it has no attributed
   * dialogue to extract one from. A real state with a real cause, not an empty
   * record, so do not render it as "not cloned yet".
   */
  get: (characterId: string, collectionId: string) =>
    api.get<CharacterVoice>(`/characters/${characterId}/voice`, {
      query: { collection_id: collectionId },
    }),

  /** POST /api/v1/admin/characters/{id}/voice/clone — idempotent upstream. */
  clone: (characterId: string, options?: { provider?: string | null; voiceSampleId?: string | null }) =>
    api.post<CharacterVoiceClone>(`/characters/${characterId}/voice/clone`, {
      provider: options?.provider ?? null,
      voice_sample_id: options?.voiceSampleId ?? null,
    }),

  /** DELETE /api/v1/admin/characters/{id}/voice/clone */
  removeClone: (characterId: string) =>
    api.delete<unknown>(`/characters/${characterId}/voice/clone`),

  /**
   * POST /api/v1/admin/characters/{id}/voice/preview — **returns the audio**.
   *
   * Not a URL and not JSON. Core synthesises to bytes and has no object store
   * to publish a preview to, so the response body *is* the MP3 or WAV. The
   * caller turns it into an object URL and must revoke that URL when done —
   * every un-revoked blob URL is a leak that lives until the tab closes.
   *
   * A 409 means the character has no ready voice clone; the message names the
   * clone action, so show it rather than a generic failure.
   */
  preview: (
    characterId: string,
    text: string,
    options: { mood?: string; language?: string } = {}
  ) =>
    api.postBlob(`/characters/${characterId}/voice/preview`, {
      text,
      mood: options.mood ?? null,
      language: options.language ?? null,
    }),
};

// --- Character participation timeline ----------------------------------------
export const characterTimelineApi = {
  /**
   * GET /api/v1/admin/collections/{id}/character-timeline
   *
   * Reads Core's `character_appearances` — the only source of who is on screen
   * when. Depictions (a poster of the character) are excluded by default,
   * because drawing one as participation puts someone in a scene they are
   * not in.
   */
  get: (
    collectionId: string,
    params: { video_id?: string | null; include_depictions?: boolean } = {}
  ) =>
    api.get<CollectionTimeline>(`/collections/${collectionId}/character-timeline`, {
      query: params,
    }),
};

// --- Look appearances (Core, time-window scoped) ----------------------------
export const lookAppearancesApi = {
  /**
   * GET /api/v1/admin/collections/{id}/look-appearances
   *
   * Returns character looks overlapping the given `[start, end]` window for
   * one asset. The Temporal Network uses this to populate the Looks layer when
   * the operator enables it — one request covers the whole window rather than
   * one per character on screen.
   */
  list: (
    collectionId: string,
    params: {
      video_id?: string | null;
      start?: number | null;
      end?: number | null;
    } = {}
  ) =>
    api.get<import('./types').LookAppearancePage>(
      `/collections/${collectionId}/look-appearances`,
      { query: params }
    ),
};

// --- Object appearances (Core proxy, Temporal Network Gap #3) ---------------
export const objectAppearancesApi = {
  /**
   * GET /api/v1/admin/collections/{id}/object-appearances
   *
   * Returns character_object_associations with labels, thumbnails, character
   * names and association type (worn_by | held_by | used_by), filtered to a
   * collection. Use `start`/`end` to scope to a time window. `timestamp` is
   * the first-frame second — not a duration.
   *
   * Requires the Core endpoint deployed in feat/kabi-phase1-development. Until
   * that SHA is active on pm_v2, Core returns 404 and this resolves to [].
   */
  list: (
    collectionId: string,
    params: {
      video_id?: string | null;
      start?: number | null;
      end?: number | null;
      character_id?: string | null;
      status?: string | null;
      limit?: number | null;
    } = {}
  ) =>
    api.get<import('./types').ObjectAppearance[]>(
      `/collections/${collectionId}/object-appearances`,
      { query: params }
    ),
};

// --- Looks (CMS Supabase) ---------------------------------------------------
export const looksApi = {
  /**
   * GET /api/v1/admin/looks — filters combine.
   *
   * `character_id` + `collection_id` together is what the product-mapping flow
   * sends: "the looks this character has in this title". Applying only one of
   * them would show a character's outfits from every film, which is the
   * over-broad list the flow exists to narrow.
   */
  list: (
    params: {
      character_id?: string | null;
      collection_id?: string | null;
      category?: string | null;
      status?: string | null;
      search?: string | null;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<Look[]>('/looks', { query: params }),

  /** GET /api/v1/admin/looks/{id} */
  get: (id: string) => api.get<Look>(`/looks/${id}`),

  /**
   * POST /api/v1/admin/looks — idempotent on (character_id, name).
   *
   * Re-creating an existing look returns that look with a 200 rather than
   * inserting a second row, so the "create the look I need" step of the mapping
   * flow is safe to repeat. Two rows for one outfit would split its products
   * between them.
   */
  create: (payload: LookCreatePayload) => api.post<Look>('/looks', payload),

  /** PATCH /api/v1/admin/looks/{id} */
  update: (id: string, payload: LookUpdatePayload) => api.patch<Look>(`/looks/${id}`, payload),

  /** DELETE /api/v1/admin/looks/{id} — hard delete; unmap products first. */
  remove: (id: string) => api.delete<DeleteResponse>(`/looks/${id}`),
};

// --- Search & Discovery -----------------------------------------------------
export const searchApi = {
  /**
   * GET /api/v1/admin/search — the unified fan-out behind the Search page.
   *
   * One query, every source that can answer it, merged and normalised by the
   * backend. The CMS never calls Core directly: characters, scenes,
   * relationships, objects and knowledge all come through the Admin API, which
   * knows which of them are Core-owned intelligence and which are filters over
   * a list Core already returns.
   *
   * **Every filter is optional and search is universal without them.** A query
   * on its own reaches every collection: scenes and objects because Core's own
   * retrieval fans out across every ingested title, characters and
   * relationships because the backend reads Core's `characters` and
   * `character_relationships` tables where Core's collection-scoped API cannot
   * help, and knowledge because it falls back to a text match when no persona
   * narrows it.
   *
   * `collection_id` is therefore a refinement, not a precondition. It is still
   * faster, and it is what unlocks the richer character manifest (dialogue
   * counts, face-track counts, thumbnails) that the plain table does not carry.
   *
   * *Browsing* — no query at all — still needs a scope, because "every scene in
   * every collection" is a catalogue dump rather than a search result. Those
   * sources come back `skipped`, never as an empty result: read `sources[]`
   * before rendering "nothing found".
   *
   * `q` is optional. Omit it to discover by filter alone — "every scene, look and
   * object for this character in this collection" is a complete request with no
   * search term in it, and each source then lists its rows in scope rather than
   * ranking them against a term nobody typed. The backend requires at least one
   * of `q` (3+ characters) or a scope, and 422s a request that has neither.
   *
   * `counts` is keyed by hit type and **only contains a key for a source that
   * actually ran**. A missing key means skipped or failed, not zero; rendering it
   * as `0` would claim the search looked and found nothing when it never looked.
   */
  everything: (params: {
    q?: string | null;
    collection_id?: string | null;
    persona_id?: string | null;
    /**
     * Narrows characters, relationships, looks and products — and changes the
     * question rather than just the filter: a named character is what the query
     * is *about*, so their row comes back whether or not the search term
     * appears in it.
     */
    character_id?: string | null;
    video_id?: string | null;
    sources?: SearchSource[];
    limit?: number;
    /** Spoiler gate: highest episode_index the viewer has watched. */
    viewer_episode_index?: number | null;
    /** Within-episode spoiler gate: playback position in seconds. */
    viewer_current_timestamp?: number | null;
  }) => api.get<UnifiedSearchResponse>('/search', { query: { ...params } }),

  /**
   * POST /api/v1/admin/search/scenes — Core's hybrid dense + lexical scene
   * retrieval, proxied.
   *
   * `collection_id` is optional, mirroring Core: omitting it makes Core search
   * every ingested title and merge by relevance.
   *
   * THE OLD WARNING HERE NO LONGER APPLIES. This used to say an unscoped search
   * cost ~5 minutes, which is why callers were told to always scope. Core's
   * retrieval has since improved and the same query now measures ~5.6–7.4
   * seconds — slow enough to need a visible loading state, nowhere near slow
   * enough to refuse the question. Scoping is still faster and worth sending
   * when the title is known.
   */
  scenes: (payload: {
    query: string;
    collection_id?: string | null;
    video_id?: string | null;
    top_k?: number;
    viewer_episode_index?: number | null;
    viewer_current_timestamp?: number | null;
    /**
     * Attach what HAPPENED in each hit's scene. Off on Core by default, so
     * omitting this changes nothing; passing it costs one extra query there
     * and fills `story_events` on every result.
     */
    include_story_events?: boolean;
  }) => api.post<SceneSearchResponse>('/search/scenes', payload),

  /**
   * POST /api/v1/admin/search/objects — Core's FashionCLIP object-track search.
   * Unscoped it searches every ingested title.
   */
  objects: (payload: {
    query: string;
    collection_id?: string | null;
    video_id?: string | null;
    top_k?: number;
  }) => api.post<ObjectSearchResponse>('/search/objects', payload),
};

// --- Cast credits ------------------------------------------------------------
//
// Two endpoints answering two different questions, and the difference is the
// whole point of the Cast Credits page:
//
//   tmdbCast(id)  — who TMDB says is in this film        (discovery)
//   list(id)      — who this platform has recorded       (the record)
//
// Merging them would make "TMDB lists this person" indistinguishable from "we
// hold this person's identity", which are the two states the page exists to
// tell apart.
//
// Images and voice are NOT here. A cast member's photos are cast anchors
// (`castAnchorsApi`) and their voice is a character voice clone
// (`characterVoicesApi`) — both already exist, and both attach to the Persona
// Character this cast member resolves to. There is no second identity store.
export const castCreditsApi = {
  /** GET /api/v1/admin/collections/{id}/cast-credits — recorded casting. */
  list: (collectionId: string) =>
    api.get<CastCredit[]>(`/collections/${collectionId}/cast-credits`),

  /**
   * GET /api/v1/admin/collections/{id}/tmdb-cast — the film's cast from TMDB.
   *
   * Looked up by the collection's **exact movie name** (`official_title`) plus
   * `release_year`. Read-only: no actor is created and no character renamed.
   *
   * A miss answers 200 with `resolved: false`, and `source` says which miss —
   * `unconfigured`, `no_title` or `not_found`. Render the difference: they call
   * for three different actions (set Core's TMDB key, set the movie name on the
   * collection, or upload cast anchor photos instead).
   */
  tmdbCast: (collectionId: string) =>
    api.get<TmdbCastResponse>(`/collections/${collectionId}/tmdb-cast`),

  /** POST /api/v1/admin/collections/{id}/cast-credits — the character must exist. */
  create: (collectionId: string, payload: CastCreditCreatePayload) =>
    api.post<CastCredit>(`/collections/${collectionId}/cast-credits`, payload),

  /** PATCH /api/v1/admin/cast-credits/{id} — billing order and status only. */
  update: (creditId: string, payload: CastCreditUpdatePayload) =>
    api.patch<CastCredit>(`/cast-credits/${creditId}`, payload),

  /** DELETE /api/v1/admin/cast-credits/{id} — removes the credit, not the person. */
  remove: (creditId: string) => api.delete<DeleteResponse>(`/cast-credits/${creditId}`),
};

// --- Governance: review queue + audit trail (CMS Supabase) ------------------
//
// Both are CMS-owned and served from Supabase through the Admin API. Core's
// `/review-queue` is a mock and its `/audit-logs` is a different, narrower
// trail; neither is called here.
export const governanceApi = {
  /** GET /api/v1/admin/review-queue — highest priority first, then oldest. */
  reviewQueue: (
    params: { status?: string; entity_type?: string; limit?: number; offset?: number } = {}
  ) => api.get<ReviewQueueItem[]>('/review-queue', { query: params }),

  /** GET /api/v1/admin/review-queue/count */
  pendingReviewCount: () => api.get<{ pending: number }>('/review-queue/count'),

  /**
   * PATCH /api/v1/admin/review-queue/{id} — record a decision.
   * The reviewer comes from the token server-side, never from this payload.
   */
  decideReview: (id: string, decision: ReviewDecisionValue, review_notes?: string) =>
    api.patch<ReviewQueueItem>(`/review-queue/${id}`, {
      decision,
      review_notes: review_notes || null,
    }),

  /** GET /api/v1/admin/audit-logs — the CMS trail, newest first. */
  auditLogs: (
    params: {
      entity_type?: string;
      entity_id?: string;
      actor_id?: string;
      action?: string;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<AuditLogEntry[]>('/audit-logs', { query: params }),
};

// --- Ingestion (Core's pipeline, via the Admin API) -------------------------
export const ingestionApi = {
  /**
   * POST /api/v1/admin/collections/{id}/ingest — multipart.
   *
   * Supply exactly one of `video_url` or `file`. Prefer the URL: a file is
   * buffered by the Admin API on its way to Core, so a feature-length upload
   * crosses the network twice.
   *
   * Returns 202 with a `job_id`. Nothing has been produced yet — poll
   * `processingJobsApi.status(job_id)` until `completed`.
   */
  ingest: (collectionId: string, form: FormData) =>
    api.postForm<IngestReceipt>(`/collections/${collectionId}/ingest`, form),
};

// --- Timeline (Core `video_segments`, via the Admin API) --------------------
export const timelineApi = {
  /**
   * GET /api/v1/admin/timeline — transcript segments in chronological order.
   *
   * Reads Core's `video_segments` table, so it needs Core's database, not just
   * Core's HTTP API. Rows appear only after an ingestion job completes; the CMS
   * never writes them.
   */
  list: (
    params: {
      video_id?: string;
      collection_id?: string;
      search?: string;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<TimelineEntry[]>('/timeline', { query: params }),

  /** GET /api/v1/admin/timeline/{id} */
  get: (id: string) => api.get<TimelineEntry>(`/timeline/${id}`),
};

// --- Objects & product matches (Core, via the Admin API) --------------------
export const objectsApi = {
  /**
   * GET /api/v1/admin/characters/{id}/wardrobe — objects grouped into looks.
   *
   * The Looks page's source of truth. Where `listByCharacter` returns a flat
   * list of object tracks, this returns them grouped into the outfits Core
   * *already found* at ingestion — `character_object_associations.outfit_number`,
   * computed by clustering worn and held items on time co-occurrence. Nothing
   * about the grouping is invented here or in the Admin API.
   *
   * Groups are per persona character by construction, so one character's outfit
   * can never contain another's shirt.
   *
   * 404 means no persona is bound to the character — a real, common state
   * (Core creates characters long before anyone provisions a persona), and
   * distinct from an empty wardrobe.
   *
   * Read `grouping_status` before rendering: `unavailable` means every object
   * arrived ungrouped because Core's association route could not be reached,
   * which is not the same claim as "this character has no outfits".
   */
  wardrobe: (characterId: string, collectionId?: string | null) =>
    api.get<WardrobeResponse>(`/characters/${characterId}/wardrobe`, {
      query: { collection_id: collectionId || undefined },
    }),

  /**
   * GET /api/v1/admin/personas/{id}/objects
   *
   * The **id-safe** object path: Core's `object_tracks` carry
   * `owner_persona_id` directly. There is deliberately no character-level
   * equivalent — Core's associations are keyed on `character_track_id`, which
   * has no id-safe join to the `character_id` the Characters page uses, and
   * bridging by name would reintroduce a defect Core has already fixed once.
   */
  listByPersona: (personaId: string) => api.get<ObjectTrack[]>(`/personas/${personaId}/objects`),

  /**
   * GET /api/v1/admin/characters/{id}/objects
   *
   * Resolves `characters.id` -> the persona bound to it
   * (`personas.character_id`, a unique FK) -> that persona's object tracks.
   * Both hops are foreign keys: no name is compared, and `character_track_id`
   * is not involved.
   *
   * 404 means no persona is bound to the character — which is different from an
   * empty list, and the UI must say so rather than showing "no objects".
   */
  listByCharacter: (characterId: string) =>
    api.get<ObjectTrack[]>(`/characters/${characterId}/objects`),

  /** GET /api/v1/admin/objects/pending — detected but not yet reviewed. */
  listPending: () => api.get<ObjectTrack[]>('/objects/pending'),

  /**
   * GET /api/v1/admin/objects/{id}/products — Core ProductMatch rows.
   * Empty means nothing has been matched, not that a merchant catalog is empty.
   */
  productsFor: (objectTrackId: string) =>
    api.get<ProductMatch[]>(`/objects/${objectTrackId}/products`),

  /**
   * Product -> Object mappings — the CMS's own curated decision about which
   * merchant product a detected object *is*.
   *
   *   GET/POST /api/v1/admin/objects/{id}/product-mappings
   *   DELETE   /api/v1/admin/objects/{id}/product-mappings/{mappingId}
   *
   * **Not the same thing as `productsFor` above.** That returns Core
   * `ProductMatch` rows — AI-detected candidates from matching the object's image
   * against Core's ingested product-image catalog. These are what a human decided.
   * The two are never merged; the UI labels each.
   *
   * The object-side twin of `commerceApi.mappings`, and deliberately a different
   * service: Commerce's mapping record has a `look_id` and no object field, so
   * there is nowhere upstream to store this. Products to map come from
   * `commerceApi.ucp.search` — there is no second product search.
   */
  productMappings: {
    /**
     * GET — one page, with an object-wide `total` so a pager needs no second call.
     * Reads only the Admin API's own database: mapped products stay visible while
     * Core and every merchant are unreachable.
     */
    list: (objectTrackId: string, params: { limit?: number; offset?: number } = {}) =>
      api.get<ProductObjectMappingPage>(`/objects/${objectTrackId}/product-mappings`, {
        query: params,
      }),

    /**
     * POST — 409 when this product is already mapped to this object, 404 when the
     * object does not exist in Core. Identifiers come straight off a UCP search
     * result; nothing here is typed by hand.
     */
    create: (objectTrackId: string, payload: ProductObjectMappingCreatePayload) =>
      api.post<ProductObjectMapping>(`/objects/${objectTrackId}/product-mappings`, payload),

    /** DELETE — hard delete, so the same product can be mapped again afterwards. */
    remove: (objectTrackId: string, mappingId: string) =>
      api.delete<DeleteResponse>(`/objects/${objectTrackId}/product-mappings/${mappingId}`),
  },

  /** GET /api/v1/admin/product-matches — every match across published videos. */
  productMatches: (params: { q?: string; category?: string } = {}) =>
    api.get<EnrichedProductMatch[]>('/product-matches', { query: params }),

  /** GET /api/v1/admin/videos/{id}/commerce-data — the whole pipeline in one call. */
  videoCommerceData: (videoId: string) =>
    api.get<VideoCommerceData>(`/videos/${videoId}/commerce-data`),

  /** GET /api/v1/admin/videos/{id}/associations */
  videoAssociations: (videoId: string) =>
    api.get<CharacterObjectAssociation[]>(`/videos/${videoId}/associations`),
};

// --- UCP Commerce (Commerce service, via the Admin API gateway) -------------
//
// Every call here goes through /api/v1/admin/commerce/*, never to the Commerce
// service directly. That is not indirection for its own sake: Commerce has no
// authentication of its own, so the Admin API's Supabase JWT check is the only
// thing standing in front of the store registry. A browser calling :8080
// directly would be an open admin surface.
export const commerceApi = {
  stores: {
    /** GET /api/v1/admin/commerce/stores */
    list: () => api.get<CommerceStore[]>('/commerce/stores'),
    /** GET /api/v1/admin/commerce/stores/{id} */
    get: (id: string) => api.get<CommerceStore>(`/commerce/stores/${id}`),
    /** POST /api/v1/admin/commerce/stores */
    create: (payload: CommerceStoreCreatePayload) =>
      api.post<CommerceStore>('/commerce/stores', payload),
    /** PATCH /api/v1/admin/commerce/stores/{id} */
    update: (id: string, payload: CommerceStoreUpdatePayload) =>
      api.patch<CommerceStore>(`/commerce/stores/${id}`, payload),
    /** DELETE /api/v1/admin/commerce/stores/{id} */
    remove: (id: string) => api.delete<void>(`/commerce/stores/${id}`),
    /**
     * POST /api/v1/admin/commerce/stores/{id}/discover-ucp
     * Reaches the merchant's own host, so it is slow and fails independently
     * of Commerce being healthy.
     */
    discoverUcp: (id: string) =>
      api.post<Record<string, unknown>>(`/commerce/stores/${id}/discover-ucp`),
    /** GET /api/v1/admin/commerce/stores/{id}/ucp-profile */
    ucpProfile: (id: string) =>
      api.get<Record<string, unknown>>(`/commerce/stores/${id}/ucp-profile`),
  },

  mappings: {
    /** GET /api/v1/admin/commerce/mappings — every filter optional. */
    list: (
      params: {
        character_id?: string;
        series_id?: string;
        episode_id?: string;
        scene_id?: string;
        look_id?: string;
        store_id?: string;
      } = {}
    ) => api.get<CharacterProductMapping[]>('/commerce/mappings', { query: params }),
    /** POST /api/v1/admin/commerce/mappings */
    create: (payload: CharacterProductMappingCreatePayload) =>
      api.post<CharacterProductMapping>('/commerce/mappings', payload),
    /** PATCH /api/v1/admin/commerce/mappings/{id} */
    update: (id: string, payload: CharacterProductMappingUpdatePayload) =>
      api.patch<CharacterProductMapping>(`/commerce/mappings/${id}`, payload),
    /** DELETE /api/v1/admin/commerce/mappings/{id} */
    remove: (id: string) => api.delete<void>(`/commerce/mappings/${id}`),
  },

  products: {
    /**
     * GET /api/v1/admin/commerce/products/search — cross-store UCP search.
     * Check `stores_failed` before rendering an empty `results` as "nothing found".
     */
    search: (params: {
      q: string;
      character?: string;
      series?: string;
      episode?: string;
      scene?: string;
      look?: string;
      limit?: number;
    }) => api.get<CommerceSearchResponse>('/commerce/products/search', { query: params }),

    /** GET /api/v1/admin/commerce/products/{store_id}/{ucp_product_id} */
    lookup: (storeId: string, ucpProductId: string) =>
      api.get<Record<string, unknown>>(
        `/commerce/products/${storeId}/${encodeURIComponent(ucpProductId)}`
      ),
  },

  ucp: {
    /** GET /api/v1/admin/commerce/ucp/platform-profile */
    platformProfile: () => api.get<UcpPlatformProfile>('/commerce/ucp/platform-profile'),
    /** GET /api/v1/admin/commerce/ucp/manifest */
    manifest: () => api.get<UcpManifest>('/commerce/ucp/manifest'),

    /**
     * POST /api/v1/admin/commerce/ucp/search — the connector-aware search.
     *
     * Each store is queried through the connector its `access_mode` names, and
     * every result carries `store_id` / `ucp_product_id` / `ucp_variant_id`,
     * which is what a product mapping is keyed on. This is the search the CMS
     * product picker uses; there is no second search implementation.
     *
     * Commerce pages with `page` / `page_size` (max 50). It has no `limit` field
     * and ignored the one this used to send, so every caller got its default 10.
     *
     * FILTERS ARE SENT, NOT APPLIED HERE. Commerce evaluates every filter
     * against the candidates it retrieved from each merchant, relaxes the most
     * negotiable ones when the combination matches nothing, and reports what it
     * dropped. Filtering the response in the browser instead would narrow one
     * page of an already-ranked, already-paged result and silently disagree
     * with `total` — which is why this passes them upstream.
     *
     * Only filters the caller set are sent. An empty `color: []` is omitted
     * rather than forwarded, because Commerce treats a present-but-empty facet
     * as no filter anyway and sending it only makes the request harder to read.
     */
    search: (query: string, limit = 20, page = 1, filters: UcpSearchFilters = {}) => {
      const body: Record<string, unknown> = { query, page, page_size: limit };
      for (const [key, value] of Object.entries(filters)) {
        if (value === undefined || value === null || value === '') continue;
        if (Array.isArray(value) && value.length === 0) continue;
        body[key] = value;
      }
      return api.post<UcpSearchResponse>('/commerce/ucp/search', body);
    },
  },
};

// --- Feedback (CMS-owned, team-only) ----------------------------------------
//
// Every route here is behind the Admin API's team check: an authenticated
// operator whose token email is not on a company domain gets a 403, on reads as
// well as writes. The frontend's own check (see `lib/feedback-access.ts`) only
// decides whether to render the nav item; it is not the control.
export const feedbackApi = {
  /**
   * GET /api/v1/admin/feedback — newest first, paginated.
   *
   * Returns the `{items, total, limit, offset, has_next}` envelope rather than a
   * bare array, because a pager needs to know whether there is a next page and
   * this table is small enough to count cheaply.
   */
  list: (
    params: {
      status?: FeedbackStatus | null;
      category?: FeedbackCategory | null;
      priority?: FeedbackPriority | null;
      mine_only?: boolean;
      limit?: number;
      offset?: number;
    } = {}
  ) => api.get<FeedbackPage>('/feedback', { query: params }),

  /** GET /api/v1/admin/feedback/{id} */
  get: (id: string) => api.get<FeedbackItem>(`/feedback/${id}`),

  /**
   * POST /api/v1/admin/feedback — multipart, so a screenshot travels with it.
   *
   * The submitter is **not** in this payload and cannot be: the backend takes it
   * from the verified access token. Sending one is rejected, not ignored.
   */
  create: (form: FormData) => api.postForm<FeedbackItem>('/feedback', form),

  /** PATCH /api/v1/admin/feedback/{id} — triage state only. */
  updateStatus: (id: string, status: FeedbackStatus) =>
    api.patch<FeedbackItem>(`/feedback/${id}`, { status }),

  /**
   * GET /api/v1/admin/feedback/{id}/screenshot — a short-lived signed URL.
   *
   * The bucket is private, so there is no permanent URL to store or embed. Each
   * view asks for a fresh link, which is what keeps a screenshot from being
   * readable by anyone who was ever sent one.
   */
  screenshotUrl: (id: string) =>
    api.get<{ url: string; expires_in: number }>(`/feedback/${id}/screenshot`),
};

// --- Speech agent -----------------------------------------------------------
//
// Core's `app/api/v1/speech_agent.py`, proxied by the Admin API at
// /api/v1/admin/speech-agent/*. The live call is a WebSocket and is not here:
// see `lib/speech-agent/live.ts`, which cannot use this client (a browser
// `WebSocket` takes no `Authorization` header).
export const speechAgentApi = {
  /**
   * GET /api/v1/admin/speech-agent/characters/{id}/status
   *
   * The character's whole speech-agent profile: persona readiness, the
   * publication gate, and the nested `voice` block with both candidates. Both
   * `chat_ready` and `voice.status === 'ready'` are required before a call.
   */
  status: (characterId: string) =>
    api.get<SpeechAgentProfile>(`/speech-agent/characters/${characterId}/status`),

  /**
   * PUT /api/v1/admin/speech-agent/characters/{id}/voice-source
   *
   * **The body is `{source, prepare}`.** It used to send `{voice_source}`,
   * which the Admin API rejects with a 422 — `source` is a required field and
   * the sent one is unknown, so the preference never saved. `'auto'` is the
   * wire value for "library first, clone as fallback"; Core stores it as null.
   *
   * `prepare` asks Core to build the chosen kind of voice in the background if
   * the character does not have one yet.
   */
  setVoiceSource: (characterId: string, source: VoiceSourceChoice, prepare = false) =>
    api.put<SpeechAgentProfile & { building: string | null }>(
      `/speech-agent/characters/${characterId}/voice-source`,
      { source, prepare }
    ),

  /**
   * POST /api/v1/admin/speech-agent/characters/{id}/prepare
   *
   * Ensures the character has a persona and a usable voice. **Background by
   * default** — the response carries `voice.status === 'building'`, and the
   * caller polls `status` until it is `ready` or `failed`. A clone takes
   * minutes, so `wait` is not offered here.
   *
   * `method` omitted lets Core use the character's own preference, then
   * `library`. All three options are **query** parameters, which is how Core
   * declares them; sent as a body they are silently ignored.
   */
  prepare: (characterId: string, options: { method?: 'library' | 'clone'; force?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (options.method) params.set('method', options.method);
    if (options.force) params.set('force', 'true');
    const query = params.toString();
    return api.post<SpeechAgentProfile>(
      `/speech-agent/characters/${characterId}/prepare${query ? `?${query}` : ''}`
    );
  },
};

export interface PremadeVoice {
  id: string;
  name: string;
  gender: string;
  accent: string;
  style: string;
}

export const premadeVoicesApi = {
  /**
   * GET /api/v1/admin/voices/premade
   * Returns ElevenLabs free-tier pre-made voices. Static list from Core.
   */
  list: () =>
    api.get<{ voices: PremadeVoice[] }>('/voices/premade'),

  /**
   * PATCH /api/v1/admin/personas/{personaId}/set-voice
   * Assigns voice_id directly to Persona.voice_id. Works for pre-made or cloned ids.
   */
  assign: (personaId: string, voiceId: string) =>
    api.patch<Record<string, unknown>>(`/personas/${personaId}/set-voice`, { voice_id: voiceId }),
};
